import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { serviceRepository } from '../repositories/service.repository';
const CACHE_DURATION = 5 * 60 * 1000;
export const useServiceStore = defineStore('service', () => {
    const services = ref([]);
    const currentService = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const lastFetched = ref(null);
    let scopeVersion = 0;
    const serviceById = computed(() => {
        return (id) => services.value.find((s) => s.id === id);
    });
    const servicesByCategory = computed(() => {
        return (categoryId) => services.value.filter((s) => s.category_id === categoryId || s.category?.id === categoryId);
    });
    const servicesByProvider = computed(() => {
        return (providerId) => services.value.filter((s) => s.provider_id === providerId || s.provider?.id === providerId);
    });
    const isCacheValid = computed(() => {
        if (!lastFetched.value)
            return false;
        return Date.now() - lastFetched.value.getTime() < CACHE_DURATION;
    });
    const totalServices = computed(() => services.value.length);
    /**
     * Fetch services with optional filtering
     * @param forceRefresh - Force refresh ignoring cache
     * @param params - Optional query parameters for filtering
     * @param params.createdBy - Filter by creator user ID. If omitted, returns all services.
     * @returns Promise with services array (filtered if params provided)
     *
     * Note: When params are provided, the store cache is updated with filtered results.
     * This is intentional - filtered views should see their filtered data in the store.
     * Different user roles will have different cached views based on their access.
     */
    async function fetchServices(forceRefresh = false, params) {
        // Only use cache for unfiltered requests when no params provided
        const hasFilters = params?.createdBy != null;
        if (!forceRefresh && isCacheValid.value && !hasFilters) {
            return services.value;
        }
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await serviceRepository.getAll(params);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            services.value = data;
            lastFetched.value = new Date();
            return data;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = err instanceof Error ? err.message : 'Error fetching services';
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    async function fetchServiceById(id) {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await serviceRepository.getById(id);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            currentService.value = data;
            const index = services.value.findIndex((s) => s.id === id);
            if (index !== -1) {
                services.value[index] = data;
            }
            return data;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = err instanceof Error ? err.message : 'Error fetching service';
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    /**
     * Create a new service.
     * Note: With the new API, schedule/availability is set separately via
     * PUT /services/:id/calendar/availability after service creation.
     * Calendar is auto-created when service is created.
     */
    async function createService(data) {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            // New API: Create service WITHOUT schedule (calendar auto-created)
            const newService = await serviceRepository.create(data);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            services.value.push(newService);
            currentService.value = newService;
            return newService;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = err instanceof Error ? err.message : 'Error creating service';
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    async function updateService(id, data) {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const updatedService = await serviceRepository.update(id, data);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            const index = services.value.findIndex((s) => s.id === id);
            if (index !== -1) {
                services.value[index] = updatedService;
            }
            if (currentService.value?.id === id) {
                currentService.value = updatedService;
            }
            return updatedService;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = err instanceof Error ? err.message : 'Error updating service';
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    async function deleteService(id) {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            await serviceRepository.delete(id);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            services.value = services.value.filter((s) => s.id !== id);
            if (currentService.value?.id === id) {
                currentService.value = null;
            }
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = err instanceof Error ? err.message : 'Error deleting service';
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    function setCurrentService(service) {
        currentService.value = service;
    }
    function clearCurrentService() {
        currentService.value = null;
    }
    function invalidateCache() {
        lastFetched.value = null;
    }
    function $reset() {
        scopeVersion++;
        services.value = [];
        currentService.value = null;
        isLoading.value = false;
        error.value = null;
        lastFetched.value = null;
    }
    return {
        services,
        currentService,
        isLoading,
        error,
        lastFetched,
        serviceById,
        servicesByCategory,
        servicesByProvider,
        isCacheValid,
        totalServices,
        fetchServices,
        fetchServiceById,
        createService,
        updateService,
        deleteService,
        setCurrentService,
        clearCurrentService,
        invalidateCache,
        $reset,
    };
});
